import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const marqueeItems = [
  'Web Design', 'Brand Identity', 'Photography', 'Videography',
  'SEO & Marketing', 'Logo Design', 'Social Media', 'E-Commerce',
  'Content Creation', 'UI/UX Design',
];

const Hero: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles: { x: number; y: number; vx: number; vy: number; size: number; opacity: number }[] = [];
    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        size: Math.random() * 1.5 + 0.5,
        opacity: Math.random() * 0.3 + 0.05,
      });
    }

    let animId: number;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(200,241,53,${p.opacity})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    const onResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', onResize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', onResize); };
  }, []);

  const doubled = [...marqueeItems, ...marqueeItems];

  return (
    <section id="hero" className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
      {/* Particle canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" />

      {/* Radial glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none"
        style={{
          width: '900px', height: '900px',
          background: 'radial-gradient(circle, rgba(200,241,53,0.04) 0%, transparent 65%)',
        }}
      />

      {/* Grid lines */}
      <div
        className="absolute inset-0 opacity-[0.025] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
          backgroundSize: '100px 100px',
        }}
      />

      {/* Rotating ring decoration */}
      <div className="absolute top-20 right-20 opacity-10 hidden lg:block pointer-events-none">
        <div className="animate-spin-slow w-32 h-32 rounded-full border border-lime" />
        <div className="absolute inset-4 animate-spin-slow rounded-full border border-lime/50" style={{ animationDirection: 'reverse', animationDuration: '12s' }} />
      </div>
      <div className="absolute bottom-32 left-16 opacity-10 hidden lg:block pointer-events-none">
        <div className="animate-pulse-ring w-20 h-20 rounded-full border border-lime" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 text-center pt-24">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="inline-flex items-center gap-2 border border-[#1e1e1e] rounded-full px-4 py-1.5 mb-10"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-lime animate-pulse" />
          <span className="font-syne text-xs text-[#aaa] uppercase tracking-widest">Web Design · Branding · Creative Media</span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="font-serif text-[clamp(3.2rem,9vw,8rem)] leading-[0.92] text-white mb-8"
        >
          Your Digital{' '}
          <span className="italic text-lime">Presence,</span>
          <br />
          <span className="italic">Beyond</span>
          {' '}✦{' '}
          <span className="text-[#888]">Limits.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="font-dm text-[#aaa] text-lg max-w-lg mx-auto mb-12 leading-relaxed"
        >
          Web design, branding, photography & creative media — all in one place. Pause or cancel anytime.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a href="#why-us" className="btn-lime px-8 py-4 text-sm font-syne font-bold">
            Explore Our Work
          </a>
          <a href="#contact" className="btn-outline px-8 py-4 text-sm">
            Book a Free Call →
          </a>
        </motion.div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="flex items-center justify-center gap-12 mt-16 pt-12 border-t border-[#111]"
        >
          {[
            { num: '50+', label: 'Projects' },
            { num: '4.9★', label: 'Rating' },
            { num: '100%', label: 'Satisfaction' },
            { num: '3×', label: 'Avg Growth' },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-serif text-2xl text-white">{s.num}</p>
              <p className="font-dm text-xs text-[#999] mt-1">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Marquee strip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="relative z-10 w-full mt-16"
      >
        <div className="overflow-hidden border-y border-[#111] py-5 bg-[#080808]/80">
          <div className="flex gap-16 animate-marquee whitespace-nowrap w-max">
            {doubled.map((item, i) => (
              <span key={i} className="font-syne text-xs font-bold text-[#2a2a2a] uppercase tracking-widest flex items-center gap-4">
                <span className="text-lime">✦</span>
                {item}
              </span>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
